import { ErrorInfo, ReactNode } from "react";

import { Client } from "@/Common/Api/Client";
import { MyleLogo } from "@/Common/Components/MyleLogo/MyleLogo";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { ErrorBoundaryPage } from "@/Common/Resources/StaticResourcesFor";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { documentsStore } from "@/Documents/Stores/DocumentsStore";
import { patientStore } from "@/Patient/Stores/PatientStore";
import { relativeStore } from "@/Relative/Stores/RelativeStore";

import "./ErrorBoundary.css";

interface ErrorBoundaryProps {
    children?: ReactNode;
}

interface ErrorBoundaryState {
    error: FrontEndError;
}

interface FrontEndError {
    route: string;
    requestHistory: string[];
    selectedPatientContactId: string;
    message: string;
    stack: string;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    private resources = new ErrorBoundaryPage();
    private stringProvider = new ResourceProvider(this.resources);

    public state: ErrorBoundaryState = {
        error: null
    };

    public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {
            error: {
                route: RouterHistory.location.pathname,
                requestHistory: Client.getOperationLogs(),
                selectedPatientContactId: patientStore ? patientStore?.getCurrentPatientContactId() : null,
                message: error.message,
                stack: error.stack
            }
        };
    }

    public async componentDidCatch(error: Error, errorInfo: ErrorInfo): Promise<void> {
        if (sessionStore.isAuthenticated()) {
            await Client.postAsync(
                ServerOperations.Server.Myle_LogFrontEndError,
                {
                    ...this.state.error,
                    componentStack: errorInfo.componentStack
                }
            );
        }
    }

    public render(): ReactNode {
        if (this.state.error === null) {
            return this.props.children;
        }

        return (
            <div className="text-center">
                <div className="errorBoundary-container">
                    <MyleLogo locale={sessionStore.locale} />
                    <h3 className="errorMessage">{this.stringProvider.GetString("UnhandledErrorLabel")}</h3>
                    <button
                        className="btn btn-primary submit"
                        onClick={() => {
                            this.setState({ error: null });
                            this.resetNavigation();
                        }}
                        type="button"
                    >
                        {this.stringProvider.GetString("TryAgainLabel")}
                    </button>
                </div>
            </div>
        );
    }

    private resetNavigation(): void {
        const route = this.state.error.route.toLowerCase();

        if (route.contains("/patient") && sessionStore?.selectedFile?.ContactFacetType === ContactFacetType.Patient && patientStore && patientStore.getCurrentPatientContactId())
            sessionStore.dispatch(LocalPatientOperations.MedicalItems.ReturnToSummary);
        else if (route.contains("/patient") && sessionStore?.selectedFile?.ContactFacetType === ContactFacetType.Relative)
            relativeStore.actions.openFile(sessionStore?.selectedFile.ContactId);
        else if (route.contains("/documents") && documentsStore && documentsStore.DocumentsModel)
            sessionStore.dispatch(LocalOperations.Documents.Tree_All_Documents_Selected);
        RouterHistory.push("myle");
    }
}

export { ErrorBoundary };
